import React, { useEffect, useRef } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { VoucherValueMetrics } from '../types/voucher';

Chart.register(...registerables);

interface VoucherValueChartProps {
    data: VoucherValueMetrics;
    chartType?: 'pie' | 'bar' | 'doughnut';
}

export const VoucherValueChart: React.FC<VoucherValueChartProps> = ({
    data,
    chartType = 'pie'
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        // Destroy existing chart
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        const config: ChartConfiguration = {
            type: chartType,
            data: {
                labels: ['CLF (Foundational)', 'DVA (Associate)', 'AIF'],
                datasets: [{
                    label: chartType === 'bar' ? 'Voucher Count' : 'Vouchers',
                    data: [data.foundationalCount, data.associateCount, (data as any).aifCount || 0],
                    backgroundColor: [
                        '#3B82F6', // Blue for CLF
                        '#10B981',  // Green for DVA
                        '#8B5CF6'   // Purple for AIF
                    ],
                    borderColor: [
                        '#2563EB',
                        '#059669',
                        '#7C3AED'
                    ],
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Voucher Distribution by Type',
                        font: {
                            size: 16,
                            weight: 'bold'
                        }
                    },
                    legend: {
                        position: 'bottom',
                        labels: {
                            padding: 20,
                            usePointStyle: true
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                const label = context.label || '';
                                const value = context.parsed;
                                const total = data.foundationalCount + data.associateCount + ((data as any).aifCount || 0);
                                const percentage = chartType === 'bar' ?
                                    ((value / total) * 100).toFixed(1) :
                                    context.label === 'CLF (Foundational)' ? data.foundationalPercentage : context.label === 'DVA (Associate)' ? data.associatePercentage : (data as any).aifPercentage || 0;
                                const voucherValue = context.label === 'CLF (Foundational)' ?
                                    data.foundationalValue : context.label === 'DVA (Associate)' ? data.associateValue : (data as any).aifValue || 0;

                                return [
                                    `${label}: ${value} vouchers`,
                                    `${percentage}% of total`,
                                    `Value: $${voucherValue.toLocaleString()}`
                                ];
                            }
                        }
                    }
                },
                ...(chartType === 'bar' && {
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                stepSize: 1
                            }
                        }
                    }
                })
            }
        };

        chartRef.current = new Chart(ctx, config);

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [data, chartType]);

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Voucher Distribution</h3>
                <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div className="bg-blue-50 p-3 rounded">
                        <div className="text-blue-600 font-medium">CLF (Foundational)</div>
                        <div className="text-2xl font-bold text-blue-900">{data.foundationalCount}</div>
                        <div className="text-blue-600">${data.foundationalValue.toLocaleString()}</div>
                        <div className="text-blue-500">{data.foundationalPercentage}% of total</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                        <div className="text-green-600 font-medium">DVA (Associate)</div>
                        <div className="text-2xl font-bold text-green-900">{data.associateCount}</div>
                        <div className="text-green-600">${data.associateValue.toLocaleString()}</div>
                        <div className="text-green-500">{data.associatePercentage}% of total</div>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                        <div className="text-purple-600 font-medium">AIF</div>
                        <div className="text-2xl font-bold text-purple-900">{(data as any).aifCount || 0}</div>
                        <div className="text-purple-600">${(((data as any).aifValue || 0)).toLocaleString()}</div>
                        <div className="text-purple-500">{(data as any).aifPercentage || 0}% of total</div>
                    </div>
                </div>
                <div className="mt-3 p-3 bg-gray-50 rounded">
                    <div className="text-gray-600 font-medium">Total Value</div>
                    <div className="text-3xl font-bold text-gray-900">${data.totalValue.toLocaleString()}</div>
                    <div className="text-gray-500">{data.foundationalCount + data.associateCount + ((data as any).aifCount || 0)} total vouchers</div>
                </div>
            </div>

            <div className="relative h-64">
                <canvas ref={canvasRef}></canvas>
            </div>
        </div>
    );
};